import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ChevronLeft, Share2, Twitter, Facebook, Linkedin, Link2, Check, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { ARTICLE_PROSE_CLASSES, stripRepeatedTitle } from '../lib/articleProse';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/couplein_logo.png';

// App Store Links
const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';

// Download Buttons Component
const DownloadButtons = ({ size = 'default', className = '' }) => (
  <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
    <a
      href={IOS_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 ${size === 'large' ? 'px-6 py-4' : 'px-4 py-3'}`}
    >
      <svg className={size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Download on the</p>
        <p className={`font-semibold ${size === 'large' ? 'text-lg' : 'text-sm'} -mt-1`}>App Store</p>
      </div>
    </a>
    <a
      href={ANDROID_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 ${size === 'large' ? 'px-6 py-4' : 'px-4 py-3'}`}
    >
      <svg className={size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor">
        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Get it on</p>
        <p className={`font-semibold ${size === 'large' ? 'text-lg' : 'text-sm'} -mt-1`}>Google Play</p>
      </div>
    </a>
  </div>
);

import Navbar from '../components/Navbar';

import NotFound from './NotFound';

const BlogPost = ({ isLegacyBlogPath = false }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);
  
  // If arrived via /blog/:slug, redirect to clean root /:slug immediately for 100% SEO integrity
  useEffect(() => {
    if (isLegacyBlogPath && slug) {
      navigate(`/${slug}`, { replace: true });
    }
  }, [isLegacyBlogPath, slug, navigate]);
  
  useEffect(() => {
    if (!isLegacyBlogPath) {
      fetchPost();
    }
  }, [slug, isLegacyBlogPath]);
  
  const fetchPost = async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const response = await axios.get(`${API}/blog/posts/${slug}`, { withCredentials: false });
      setPost(response.data);
      
      // Fetch related posts
      const allPosts = await axios.get(`${API}/blog/posts`, { withCredentials: false });
      const related = (allPosts.data.posts || [])
        .filter(p => p.slug !== slug && p.published)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (error) {
      console.error('Error fetching post:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };
  
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Article link copied to clipboard!');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  const shareLinks = [
    { icon: Twitter, url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post?.title || '')}`, label: 'Twitter / X' },
    { icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, label: 'Facebook' },
    { icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, label: 'LinkedIn' },
    { icon: MessageCircle, url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${post?.title || 'CoupleIn Article'} - ${shareUrl}`)}`, label: 'WhatsApp' },
  ];
  
  if (isLegacyBlogPath) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 pb-16 max-w-3xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="aspect-video bg-gray-200 rounded-2xl" />
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (notFound || !post) {
    return <NotFound />;
  }
  
  const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://coupleinapp.com').replace(/\/$/, '');
  const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const seoDescription = post.meta_description || post.excerpt || stripHtml(post.content).slice(0, 160);
  const seoTitle = post.meta_title || post.title;
  
  // Combine custom SEO keywords, category, and title for maximum search engine discovery
  const keywordList = [
    post.keywords,
    post.category,
    'couple relationship',
    'relationship advice',
    post.title.toLowerCase()
  ].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/${post.slug}`}
        image={post.image || undefined}
        type="article"
        keywords={keywordList}
        article={{
          publishedTime: post.created_at,
          modifiedTime: post.updated_at || post.created_at,
          author: 'CoupleIn Team',
          section: post.category || 'Relationships',
        }}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: seoDescription,
          image: post.image || undefined,
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { '@type': 'Organization', name: 'CoupleIn' },
          publisher: {
            '@type': 'Organization',
            name: 'CoupleIn',
            logo: { '@type': 'ImageObject', url: LOGO_URL },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/${post.slug}` },
          url: `${SITE_URL}/${post.slug}`,
          articleSection: post.category || 'Relationships',
          wordCount: stripHtml(post.content).split(/\s+/).length,
        }}
      />
      <Navbar />
      
      {/* Article */}
      <article className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-600 mb-6">
            <ChevronLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {post.category && (
              <span className="inline-block bg-pink-100 text-pink-600 text-sm font-medium px-4 py-1 rounded-full mb-4">
                {post.category}
              </span>
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-gray-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {post.read_time || '5'} min read
              </span>
            </div>
          </motion.header>
          
          {/* Featured Image */}
          {post.image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8 rounded-2xl overflow-hidden"
            >
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full aspect-video object-cover"
              />
            </motion.div>
          )}
          
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={ARTICLE_PROSE_CLASSES}
            dangerouslySetInnerHTML={{ __html: stripRepeatedTitle(post.content, post.title) }}
          />
          
          {/* Share */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
                <Share2 className="w-5 h-5 text-pink-500" />
                Share this article with your partner or friends:
              </span>
              <div className="flex items-center gap-2">
                {shareLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 hover:bg-pink-100 rounded-full flex items-center justify-center text-gray-600 hover:text-pink-500 transition-colors shadow-xs"
                    aria-label={`Share on ${link.label}`}
                    title={`Share on ${link.label}`}
                  >
                    <link.icon className="w-4 h-4" />
                  </a>
                ))}

                {/* Copy Link Button */}
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 hover:bg-pink-100 text-gray-700 hover:text-pink-600 rounded-full text-xs font-semibold transition-colors shadow-xs"
                  title="Copy direct article URL"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Link2 className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>
      
      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">More articles you might like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/${relatedPost.slug}`}
                  className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <div className="aspect-video bg-gradient-to-br from-pink-100 to-orange-100 overflow-hidden">
                    {relatedPost.image ? (
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">
                        {relatedPost.emoji || '💕'}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 group-hover:text-pink-500 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-pink-500 to-orange-400">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to strengthen your relationship?</h2>
          <p className="text-lg opacity-90 mb-8">Download CoupleIn and start growing closer today.</p>
          <DownloadButtons size="large" className="justify-center" />
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} CoupleIn. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;